(() => {
  "use strict";

  if (window.FexxPremiumCartLoaded) return;
  window.FexxPremiumCartLoaded = true;

  const ROOT = window.Shopify?.routes?.root || "/";
  const reduceMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const jsonRequest = async (url, options = {}) => {
    const response = await fetch(url, options);
    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok) {
      throw new Error(
        payload?.description ||
        payload?.message ||
        "Não foi possível atualizar o carrinho."
      );
    }

    return payload;
  };

  const fetchCart = () => jsonRequest(`${ROOT}cart.js`, {
    headers: { Accept: "application/json" }
  });

  const uniqueSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections }
      })
    );
    return [...new Set(sections.filter(Boolean))].slice(0, 5);
  };

  const refreshCartWithSections = () => jsonRequest(`${ROOT}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify({
      updates: {},
      sections: uniqueSections(),
      sections_url: window.location.pathname
    })
  });

  const updateCartLines = (updates) => jsonRequest(`${ROOT}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify({
      updates,
      sections: uniqueSections(),
      sections_url: window.location.pathname
    })
  });

  const dispatchCartChange = (cart, baseEvent = "fexx:cart-change") => {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent, cart }
      })
    );
  };

  const replaceTokens = (template, values) => {
    return Object.entries(values).reduce((text, [key, value]) => {
      return text.replaceAll(`{${key}}`, value ?? "");
    }, template || "");
  };

  const rewardItems = (cart) => (cart?.items || []).filter(
    (item) => item?.properties?._fexx_reward
  );

  const rewardItemsForGoal = (cart, goalId) => rewardItems(cart).filter(
    (item) => String(item?.properties?._fexx_reward_goal || "") === String(goalId)
  );

  const replaceGift = async ({ variantId, goalId, goalTitle }) => {
    const cart = await fetchCart();
    const currentRewards = rewardItemsForGoal(cart, goalId);
    const alreadySelected = currentRewards.length === 1
      && Number(currentRewards[0].variant_id) === Number(variantId)
      && Number(currentRewards[0].quantity) === 1;

    if (alreadySelected) return refreshCartWithSections();

    await jsonRequest(`${ROOT}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties: {
            _fexx_reward: "true",
            _fexx_reward_goal: String(goalId),
            _fexx_reward_title: String(goalTitle || "Brinde")
          }
        }]
      })
    });

    if (currentRewards.length) {
      const removals = Object.fromEntries(currentRewards.map((item) => [item.key, 0]));
      await jsonRequest(`${ROOT}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ updates: removals })
      });
    }

    return refreshCartWithSections();
  };

  class FexxCartReservation extends HTMLElement {
    connectedCallback() {
      this.timeElement = this.querySelector("[data-fexx-reservation-time]");
      this.duration = Math.max(1, Number(this.dataset.duration || 0));
      this.storageKey = this.dataset.storageKey || "fexx-cart-reservation";
      this.endTime = this.getEndTime();
      this.render();
      this.interval = window.setInterval(() => this.render(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
    }

    getEndTime() {
      const now = Date.now();
      try {
        const stored = Number(window.sessionStorage.getItem(this.storageKey));
        if (Number.isFinite(stored) && stored > now) return stored;
        const end = now + this.duration * 1000;
        window.sessionStorage.setItem(this.storageKey, String(end));
        return end;
      } catch (error) {
        return now + this.duration * 1000;
      }
    }

    render() {
      if (!this.timeElement) return;
      const remaining = Math.max(0, Math.ceil((this.endTime - Date.now()) / 1000));
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      this.timeElement.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      this.classList.toggle("is-expired", remaining === 0);
      if (remaining === 0) window.clearInterval(this.interval);
    }
  }

  class FexxGiftPicker extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDrawerShow = this.onDrawerShow.bind(this);
      this.previousFocus = null;
      this.busy = false;
    }

    connectedCallback() {
      this.dialog = this.querySelector("[data-fexx-gift-dialog]");
      this.drawer = this.closest("cart-drawer");
      this.goalId = this.dataset.goalId || "";
      this.goalTitle = this.dataset.goalTitle || "Brinde";
      this.sessionKey = this.dataset.sessionKey || `fexx-gift-picker-${this.goalId}`;
      this.addEventListener("click", this.onClick);
      document.addEventListener("keydown", this.onKeydown);
      this.drawer?.addEventListener("dialog:after-show", this.onDrawerShow);
      window.setTimeout(() => this.maybeAutoAct(), 160);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      document.removeEventListener("keydown", this.onKeydown);
      this.drawer?.removeEventListener("dialog:after-show", this.onDrawerShow);
      this.unlockPage();
    }

    get isUnlocked() {
      return this.dataset.unlocked === "true" && !this.hidden;
    }

    get selectedVariant() {
      return Number(this.dataset.selectedVariant || 0);
    }

    get optionButtons() {
      return [...this.querySelectorAll("[data-fexx-select-gift]:not([disabled])")];
    }

    markSeen() {
      try {
        window.sessionStorage.setItem(this.sessionKey, "seen");
      } catch (error) {
        // Session storage is optional.
      }
    }

    resetAutoOpen() {
      try {
        window.sessionStorage.removeItem(this.sessionKey);
      } catch (error) {
        // Session storage is optional.
      }
    }

    wasSeen() {
      try {
        return window.sessionStorage.getItem(this.sessionKey) === "seen";
      } catch (error) {
        return false;
      }
    }

    onDrawerShow() {
      window.setTimeout(() => this.maybeAutoAct(), 120);
    }

    maybeAutoAct(forceOpen = false) {
      if (this.drawer && !this.drawer.hasAttribute("open")) return;
      if (!this.isUnlocked || this.selectedVariant) return;
      const buttons = this.optionButtons;
      if (!buttons.length) return;

      if (this.dataset.autoSelectSingle === "true" && buttons.length === 1) {
        this.selectGift(buttons[0]);
        return;
      }

      if (this.dataset.autoOpen === "true" && (forceOpen || !this.wasSeen())) {
        this.open();
      }
    }

    open() {
      if (!this.dialog || !this.isUnlocked) return;
      this.previousFocus = document.activeElement;
      this.dialog.hidden = false;
      this.classList.add("is-open");
      document.documentElement.classList.add("fexx-gift-picker-open");
      this.markSeen();
      window.requestAnimationFrame(() => {
        this.querySelector("[data-fexx-close-gift]")?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.dialog) return;
      this.dialog.hidden = true;
      this.classList.remove("is-open");
      this.unlockPage();
      if (this.previousFocus instanceof HTMLElement) {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    unlockPage() {
      if (!document.querySelector("fexx-gift-picker.is-open")) {
        document.documentElement.classList.remove("fexx-gift-picker-open");
      }
    }

    onKeydown(event) {
      if (event.key === "Escape" && this.classList.contains("is-open")) {
        event.preventDefault();
        this.close();
      }
    }

    onClick(event) {
      const close = event.target.closest("[data-fexx-close-gift]");
      if (close) {
        event.preventDefault();
        this.close();
        return;
      }

      const select = event.target.closest("[data-fexx-select-gift]");
      if (select) {
        event.preventDefault();
        this.selectGift(select);
      }
    }

    async selectGift(button) {
      if (this.busy || !this.isUnlocked || button.disabled) return;
      const variantId = Number(button.dataset.variantId || 0);
      if (!variantId) return;

      if (variantId === this.selectedVariant) {
        this.close();
        return;
      }

      this.busy = true;
      this.setAttribute("aria-busy", "true");
      button.classList.add("is-loading");
      button.setAttribute("aria-busy", "true");

      try {
        const updatedCart = await replaceGift({
          variantId,
          goalId: this.goalId,
          goalTitle: this.goalTitle
        });
        dispatchCartChange(updatedCart, "fexx:gift-selected");
      } catch (error) {
        button.classList.remove("is-loading");
        button.removeAttribute("aria-busy");
        this.showError(error.message);
        this.busy = false;
        this.removeAttribute("aria-busy");
      }
    }

    showError(message) {
      let error = this.querySelector("[data-fexx-gift-error]");
      if (!error) {
        error = document.createElement("p");
        error.className = "fcd-gift-picker__error";
        error.dataset.fexxGiftError = "";
        this.querySelector(".fcd-gift-picker__panel")?.append(error);
      }
      error.textContent = message || "Não foi possível adicionar o brinde.";
    }
  }

  class FexxCartGoals extends HTMLElement {
    constructor() {
      super();
      this.currentCart = null;
      this.reconcilingRewards = false;
      this.reconcileTimer = null;
      this.completionTimer = null;
      this.hasRendered = false;
      this.wasComplete = false;
      this.onCartChange = this.onCartChange.bind(this);
      this.onRefresh = this.onRefresh.bind(this);
      this.onFirstDrawerShow = this.onFirstDrawerShow.bind(this);
    }

    connectedCallback() {
      this.drawer = this.closest("cart-drawer") || document;
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || "BRL";
      this.quantitySingular = this.dataset.quantitySingular || "produto";
      this.quantityPlural = this.dataset.quantityPlural || "produtos";
      this.completeMessage = this.dataset.completeMessage || "Todos os benefícios foram liberados.";
      this.beforeTemplate = this.dataset.beforeTemplate || "Faltam {amount} para liberar {goal}";
      this.afterTemplate = this.dataset.afterTemplate || "{unlocked} liberado. Faltam {amount} para {goal}";
      this.unlockAnimation = this.dataset.unlockAnimation || "subtle";
      this.completionAnimation = this.dataset.completionAnimation || "confetti";
      this.animationIntensity = Math.max(1, Math.min(3, Number(this.dataset.animationIntensity || 2)));
      this.statusElement = this.querySelector("[data-fexx-goals-status]");
      this.progressElement = this.querySelector("[data-fexx-progress]");
      this.goals = [...this.querySelectorAll("[data-fexx-goal]")]
        .map((element) => ({
          id: element.dataset.fexxGoal,
          triggerType: element.dataset.triggerType === "quantity" ? "quantity" : "amount",
          threshold: Number(element.dataset.threshold || 0),
          title: element.dataset.title || "benefício",
          giftEnabled: element.dataset.giftEnabled === "true",
          removeGiftIfUnqualified: element.dataset.removeGiftIfUnqualified !== "false",
          allowedVariants: new Set(
            [...element.querySelectorAll("[data-fexx-allowed-gift]")]
              .map((item) => Number(item.dataset.fexxAllowedGift || 0))
              .filter(Boolean)
          ),
          marker: this.querySelector(`[data-fexx-marker="${element.dataset.fexxGoal}"]`),
          label: this.querySelector(`[data-fexx-goal-label="${element.dataset.fexxGoal}"]`),
          virtualGift: null,
          giftSummary: null,
          giftPicker: null,
          wasUnlocked: false
        }))
        .filter((goal) => goal.threshold > 0);

      const singleTriggerType = this.goals.every((goal) => goal.triggerType === this.goals[0]?.triggerType);
      if (singleTriggerType) this.goals.sort((a, b) => a.threshold - b.threshold);

      this.goals.forEach((goal, index) => {
        const position = ((index + 1) / this.goals.length) * 100;
        goal.marker?.style.setProperty("--goal-position", `${position}%`);
        goal.label?.style.setProperty("--goal-position", `${position}%`);
      });

      document.addEventListener("cart:change", this.onCartChange);
      document.addEventListener("cart:refresh", this.onRefresh);

      const initialTotal = Number(this.dataset.currentTotal || 0);
      const initialQuantity = Number(this.dataset.currentQuantity || 0);
      this.render({ items: [], total_price: initialTotal }, initialTotal, initialQuantity, true);
      if (this.drawer && this.drawer !== document) {
        this.drawer.addEventListener("dialog:after-show", this.onFirstDrawerShow, { once: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener("cart:change", this.onCartChange);
      document.removeEventListener("cart:refresh", this.onRefresh);
      if (this.drawer && this.drawer !== document) {
        this.drawer.removeEventListener("dialog:after-show", this.onFirstDrawerShow);
      }
      window.clearTimeout(this.reconcileTimer);
      window.clearTimeout(this.completionTimer);
    }

    resolveDynamicElements() {
      this.goals.forEach((goal) => {
        goal.virtualGift = this.drawer.querySelector(`[data-fexx-virtual-gift="${goal.id}"]`);
        goal.giftSummary = this.drawer.querySelector(`[data-fexx-gift-summary="${goal.id}"]`);
        goal.giftPicker = this.drawer.querySelector(`[data-fexx-gift-picker="${goal.id}"]`);
      });
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: this.currency,
          minimumFractionDigits: 2
        }).format(Math.max(0, cents) / 100);
      } catch (error) {
        return `R$ ${(Math.max(0, cents) / 100).toFixed(2).replace(".", ",")}`;
      }
    }

    qualifyingTotal(cart) {
      const rewardTotal = rewardItems(cart).reduce(
        (sum, item) => sum + Number(item.final_line_price || 0),
        0
      );
      return Math.max(0, Number(cart?.total_price || 0) - rewardTotal);
    }

    qualifyingQuantity(cart) {
      const rewardQuantity = rewardItems(cart).reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0
      );
      const cartQuantity = (cart?.items || []).reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0
      );
      return Math.max(0, cartQuantity - rewardQuantity);
    }

    metricFor(goal, total, quantity) {
      return goal.triggerType === "quantity" ? quantity : total;
    }

    isUnlocked(goal, total, quantity) {
      return this.metricFor(goal, total, quantity) >= goal.threshold;
    }

    remainingFor(goal, total, quantity) {
      return Math.max(0, goal.threshold - this.metricFor(goal, total, quantity));
    }

    formatRemaining(goal, total, quantity) {
      const remaining = this.remainingFor(goal, total, quantity);
      if (goal.triggerType === "quantity") {
        const unit = remaining === 1 ? this.quantitySingular : this.quantityPlural;
        return `${remaining} ${unit}`;
      }
      return this.formatMoney(remaining);
    }

    progressFor(total, quantity) {
      if (!this.goals.length) return 0;
      const segment = 100 / this.goals.length;
      const previousThreshold = { amount: 0, quantity: 0 };

      for (let index = 0; index < this.goals.length; index += 1) {
        const goal = this.goals[index];
        const metric = this.metricFor(goal, total, quantity);
        const previous = previousThreshold[goal.triggerType] || 0;

        if (metric >= goal.threshold) {
          previousThreshold[goal.triggerType] = Math.max(previous, goal.threshold);
          continue;
        }

        const range = Math.max(1, goal.threshold - previous);
        const fraction = Math.max(0, Math.min(1, (metric - previous) / range));
        return Math.min(100, index * segment + fraction * segment);
      }
      return 100;
    }

    render(cart, forcedTotal = null, forcedQuantity = null, initial = false) {
      this.currentCart = cart;
      this.resolveDynamicElements();
      const total = forcedTotal === null ? this.qualifyingTotal(cart) : forcedTotal;
      const quantity = forcedQuantity === null ? this.qualifyingQuantity(cart) : forcedQuantity;
      const progress = this.progressFor(total, quantity);

      if (this.progressElement) this.progressElement.style.width = `${progress}%`;

      const nextGoalIndex = this.goals.findIndex((goal) => !this.isUnlocked(goal, total, quantity));
      const nextGoal = nextGoalIndex === -1 ? null : this.goals[nextGoalIndex];
      const lastUnlocked = nextGoalIndex > 0
        ? [...this.goals.slice(0, nextGoalIndex)].reverse().find((goal) => this.isUnlocked(goal, total, quantity))
        : null;

      const isComplete = Boolean(this.goals.length && !nextGoal);
      const justCompleted = !initial && this.hasRendered && isComplete && !this.wasComplete;

      this.classList.toggle("is-complete", isComplete);

      if (justCompleted) {
        window.clearTimeout(this.completionTimer);
        this.classList.remove("just-completed");
        void this.offsetWidth;
        this.classList.add("just-completed");
        this.completionTimer = window.setTimeout(() => {
          this.classList.remove("just-completed");
        }, 1100);
        this.celebrate(this.completionAnimation, this);
      } else if (!isComplete) {
        this.classList.remove("just-completed");
      }

      if (this.statusElement) {
        if (!nextGoal && this.goals.length) {
          this.statusElement.textContent = this.completeMessage;
        } else if (nextGoal && lastUnlocked) {
          const remaining = this.remainingFor(nextGoal, total, quantity);
          this.statusElement.textContent = replaceTokens(this.afterTemplate, {
            unlocked: lastUnlocked.title,
            amount: this.formatRemaining(nextGoal, total, quantity),
            count: nextGoal.triggerType === "quantity" ? remaining : "",
            unit: nextGoal.triggerType === "quantity"
              ? (remaining === 1 ? this.quantitySingular : this.quantityPlural)
              : "",
            goal: nextGoal.title
          });
        } else if (nextGoal) {
          const remaining = this.remainingFor(nextGoal, total, quantity);
          this.statusElement.textContent = replaceTokens(this.beforeTemplate, {
            amount: this.formatRemaining(nextGoal, total, quantity),
            count: nextGoal.triggerType === "quantity" ? remaining : "",
            unit: nextGoal.triggerType === "quantity"
              ? (remaining === 1 ? this.quantitySingular : this.quantityPlural)
              : "",
            goal: nextGoal.title
          });
        }
      }

      this.goals.forEach((goal) => {
        const unlocked = this.isUnlocked(goal, total, quantity);
        const justUnlocked = !initial && this.hasRendered && unlocked && !goal.wasUnlocked;
        goal.marker?.classList.toggle("is-reached", unlocked);
        goal.label?.classList.toggle("is-reached", unlocked);

        [goal.virtualGift, goal.giftSummary].forEach((element) => {
          if (!element) return;
          element.hidden = !unlocked;
          element.setAttribute("aria-hidden", unlocked ? "false" : "true");
          element.classList.toggle("is-visible", unlocked);
          if (justUnlocked) {
            element.classList.remove("just-unlocked");
            void element.offsetWidth;
            element.classList.add("just-unlocked");
            window.setTimeout(() => element?.classList.remove("just-unlocked"), 800);
          }
        });

        if (goal.giftPicker) {
          goal.giftPicker.hidden = !unlocked;
          goal.giftPicker.dataset.unlocked = String(unlocked);

          if (!unlocked) {
            goal.giftPicker.resetAutoOpen?.();
          } else if (justUnlocked) {
            window.setTimeout(() => goal.giftPicker?.maybeAutoAct?.(true), 80);
          }
        }

        if (justUnlocked && !justCompleted) {
          this.celebrate(this.unlockAnimation, goal.marker || this);
        }

        goal.wasUnlocked = unlocked;
      });

      this.wasComplete = isComplete;
      this.hasRendered = !initial || this.hasRendered;
      if (!initial) this.scheduleRewardReconciliation(cart, total, quantity);
    }

    celebrate(type, origin) {
      if (!type || type === "none" || type === "subtle" || reduceMotion()) return;
      const drawerRect = this.drawer.getBoundingClientRect?.();
      if (!drawerRect || drawerRect.width < 10) return;

      const originRect = origin?.getBoundingClientRect?.() || this.getBoundingClientRect();
      const centerX = originRect.left + originRect.width / 2;
      const centerY = originRect.top + originRect.height / 2;
      const layer = document.createElement("div");
      layer.className = `fcd-celebration-layer fcd-celebration-layer--${type}`;
      layer.setAttribute("aria-hidden", "true");
      layer.style.setProperty("--origin-x", `${centerX}px`);
      layer.style.setProperty("--origin-y", `${centerY}px`);

      const styles = getComputedStyle(this.drawer);
      const colors = [
        styles.getPropertyValue("--fcd-celebration-1").trim() || "#171719",
        styles.getPropertyValue("--fcd-celebration-2").trim() || "#d6ad60",
        styles.getPropertyValue("--fcd-celebration-3").trim() || "#4e7dff"
      ];
      colors.forEach((color, index) => {
        layer.style.setProperty(`--fcd-celebration-${index + 1}`, color);
      });

      const count = type === "confetti"
        ? 22 + this.animationIntensity * 14
        : type === "sparkles"
          ? 10 + this.animationIntensity * 6
          : 1;

      for (let index = 0; index < count; index += 1) {
        const particle = document.createElement("span");
        particle.className = "fcd-celebration-particle";
        const angle = (Math.PI * 2 * index) / Math.max(1, count) + Math.random() * 0.55;
        const distance = (type === "confetti" ? 95 : 55) + Math.random() * (55 * this.animationIntensity);
        const endX = Math.cos(angle) * distance;
        const endY = Math.sin(angle) * distance + (type === "confetti" ? 150 + Math.random() * 120 : 0);
        particle.style.setProperty("--x", `${endX}px`);
        particle.style.setProperty("--y", `${endY}px`);
        particle.style.setProperty("--delay", `${Math.random() * 160}ms`);
        particle.style.setProperty("--duration", `${850 + Math.random() * 650}ms`);
        particle.style.setProperty("--rotation", `${Math.round(Math.random() * 720 - 360)}deg`);
        particle.style.setProperty("--particle-color", colors[index % colors.length]);
        particle.style.setProperty("--particle-size", `${type === "sparkles" ? 5 + Math.random() * 5 : 4 + Math.random() * 6}px`);
        layer.append(particle);
      }

      if (type === "burst") {
        const ring = document.createElement("span");
        ring.className = "fcd-celebration-burst";
        ring.innerHTML = '<svg viewBox="0 0 28 28" fill="none"><circle cx="14" cy="14" r="12" fill="currentColor"/><path d="m8.8 14.1 3.1 3.1 7.2-7.4" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        layer.append(ring);
      }

      document.body.append(layer);
      window.setTimeout(() => layer.remove(), 1900);
    }

    onCartChange(event) {
      if (!event.detail?.cart) return;
      window.requestAnimationFrame(() => this.render(event.detail.cart));
    }


    onFirstDrawerShow() {
      this.onRefresh();
    }

    async onRefresh() {
      try {
        const cart = await fetchCart();
        this.render(cart);
      } catch (error) {
        this.showError(error.message);
      }
    }

    scheduleRewardReconciliation(cart, total, quantity) {
      if (this.reconcilingRewards) return;
      window.clearTimeout(this.reconcileTimer);
      this.reconcileTimer = window.setTimeout(() => {
        this.reconcileRewards(cart, total, quantity);
      }, 120);
    }

    async reconcileRewards(cart, total, quantity) {
      if (this.reconcilingRewards) return;
      const updates = {};
      const keptByGoal = new Set();
      const goalsById = new Map(this.goals.map((goal) => [String(goal.id), goal]));

      rewardItems(cart).forEach((item) => {
        const goalId = String(item?.properties?._fexx_reward_goal || "");
        const goal = goalsById.get(goalId);
        const unlocked = goal ? this.isUnlocked(goal, total, quantity) : false;
        const allowed = goal?.allowedVariants?.size
          ? goal.allowedVariants.has(Number(item.variant_id))
          : true;

        if (!goal || !goal.giftEnabled || !allowed || (goal.removeGiftIfUnqualified && !unlocked)) {
          updates[item.key] = 0;
          return;
        }

        if (keptByGoal.has(goalId)) {
          updates[item.key] = 0;
          return;
        }

        keptByGoal.add(goalId);
        if (Number(item.quantity) !== 1) updates[item.key] = 1;
      });

      if (!Object.keys(updates).length) return;

      this.reconcilingRewards = true;
      this.setAttribute("aria-busy", "true");

      try {
        const updatedCart = await updateCartLines(updates);
        dispatchCartChange(updatedCart, "fexx:reward-reconciliation");
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.reconcilingRewards = false;
        this.removeAttribute("aria-busy");
      }
    }

    showError(message) {
      if (this.statusElement) {
        this.statusElement.textContent = message || "Não foi possível atualizar os benefícios.";
      }
    }
  }

  if (!customElements.get("fexx-cart-reservation")) {
    customElements.define("fexx-cart-reservation", FexxCartReservation);
  }

  if (!customElements.get("fexx-gift-picker")) {
    customElements.define("fexx-gift-picker", FexxGiftPicker);
  }

  if (!customElements.get("fexx-cart-goals")) {
    customElements.define("fexx-cart-goals", FexxCartGoals);
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-fexx-open-gift]");
    if (!trigger) return;
    event.preventDefault();
    const goalId = trigger.dataset.fexxOpenGift;
    const drawer = trigger.closest("cart-drawer") || document;
    drawer.querySelector(`[data-fexx-gift-picker="${goalId}"]`)?.open();
  });

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-fexx-quantity]");
    if (!button) return;

    const quantityWrapper = button.closest("line-item-quantity");
    const input = quantityWrapper?.querySelector("input[data-line-key]");
    if (!input) return;

    event.preventDefault();
    const currentValue = Number.parseInt(input.value, 10) || 1;
    const min = Number.parseInt(input.min, 10) || 1;
    const max = input.max ? Number.parseInt(input.max, 10) : Number.POSITIVE_INFINITY;
    const nextValue = button.dataset.fexxQuantity === "plus"
      ? Math.min(max, currentValue + 1)
      : Math.max(min, currentValue - 1);

    if (nextValue === currentValue) return;
    input.value = String(nextValue);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  document.addEventListener("click", (event) => {
    const checkout = event.target.closest("[data-fexx-checkout]");
    if (!checkout || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const href = checkout.getAttribute("href");
    if (!href || checkout.classList.contains("is-loading")) return;

    event.preventDefault();
    const label = checkout.querySelector("[data-fexx-checkout-label]");
    checkout.classList.add("is-loading");
    checkout.setAttribute("aria-busy", "true");
    if (label && checkout.dataset.loadingLabel) label.textContent = checkout.dataset.loadingLabel;

    if (typeof window.yampiClick === "function") {
      window.yampiClick();
      return;
    }

    window.setTimeout(() => {
      window.location.href = href;
    }, 260);
  });
})();
